// InfoCard Component - Displays cards in the two-column grid (editor view)
import { cssVars } from './editorConstants';
import { CardExtrasDisplay } from './CardExtras';

export default function InfoCard({ card }) {
  console.log('[InfoCard] Rendering card type:', card?.type);

  return (
    <div 
      style={{
        background: cssVars.bgWhite,
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}
    >
      {/* Card Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          background: cssVars.bg,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem'
        }}>
          {card.icon}
        </div>
        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: cssVars.accent }}>
          {card.title}
        </span>
      </div>

      {/* Card Content */}
      {card.type === 'ADVICE' && (
        <p style={{ fontSize: '0.95rem', color: cssVars.textSecondary, lineHeight: '1.6' }}>
          {card.content?.text}
        </p>
      )}

      {card.type === 'JOURNEY_TIMELINE' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', position: 'relative' }}>
          {(card.content?.items || []).map((item, i, arr) => (
            <div 
              key={i} 
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '14px',
                padding: '12px 0',
                paddingTop: i === 0 ? '0' : '12px',
                paddingBottom: i === arr.length - 1 ? '0' : '12px',
                position: 'relative'
              }}
            >
              {i < arr.length - 1 && (
                <div style={{
                  position: 'absolute',
                  left: '4px',
                  top: i === 0 ? '5px' : '17px',
                  width: '2px',
                  height: 'calc(100% - 8px)',
                  background: cssVars.border
                }} />
              )}
              <div style={{
                width: '10px',
                height: '10px',
                background: item.is_current ? cssVars.accent : cssVars.border,
                borderRadius: '50%',
                flexShrink: 0,
                marginTop: '5px',
                position: 'relative',
                zIndex: 1
              }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {/* Company name - bold */}
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: cssVars.text }}>
                  {item.title || item.company}
                </span>
                {/* Role - light gray */}
                {(item.subtitle || item.role) && (
                  <span style={{ fontSize: '0.8rem', color: cssVars.textMuted, fontWeight: '400' }}>
                    {item.subtitle || item.role}
                  </span>
                )}
                {/* Description/one-liner - light gray, smaller */}
                {item.description && (
                  <span style={{ 
                    fontSize: '0.78rem', 
                    color: cssVars.textMuted, 
                    fontWeight: '400',
                    lineHeight: '1.4',
                    marginTop: '2px',
                    opacity: 0.85
                  }}>
                    {item.description}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {card.type === 'CONTENT_LIST' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(card.content?.items || []).map((item, i) => {
            const isPodcast = item.type === 'podcast';
            const isNewsletter = item.type === 'newsletter';
            const isBook = item.type === 'book';
            const hasLink = item.url;
            const isLinkedContent = (isPodcast || isNewsletter || isBook) && hasLink;
            
            // Color scheme based on type
            const colorScheme = isPodcast 
              ? { bg: 'linear-gradient(135deg, #FDF4FF 0%, #FAE8FF 100%)', border: 'rgba(168, 85, 247, 0.15)', text: '#7C3AED', accent: '#A855F7', iconBg: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)' }
              : isNewsletter 
                ? { bg: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)', border: 'rgba(249, 115, 22, 0.15)', text: '#C2410C', accent: '#F97316', iconBg: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }
                : isBook
                  ? { bg: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', border: 'rgba(16, 185, 129, 0.15)', text: '#065F46', accent: '#10B981', iconBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }
                  : null;
            
            return (
              <div 
                key={i} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '12px',
                  padding: isLinkedContent ? '10px 14px' : '0',
                  background: isLinkedContent ? colorScheme.bg : 'transparent',
                  borderRadius: isLinkedContent ? '12px' : '0',
                  border: isLinkedContent ? `1px solid ${colorScheme.border}` : 'none',
                }}
              >
                {/* Type icon for linked content */}
                {isLinkedContent ? (
                  <div style={{
                    width: '28px',
                    height: '28px',
                    background: colorScheme.iconBg,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {isPodcast ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M12 1a9 9 0 0 0-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7a9 9 0 0 0-9-9z"/>
                      </svg>
                    ) : isNewsletter ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6" fill="none" stroke="white" strokeWidth="2"/>
                      </svg>
                    ) : isBook ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" fill="white"/>
                      </svg>
                    ) : null}
                  </div>
                ) : (
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: cssVars.textMuted, 
                    minWidth: '55px',
                    textTransform: 'lowercase'
                  }}>
                    {item.type}
                  </span>
                )}
                
                {/* Name */}
                <div style={{ flex: 1 }}>
                  <span style={{ 
                    fontWeight: '500', 
                    color: isLinkedContent ? colorScheme.text : cssVars.text, 
                    fontSize: '0.9rem' 
                  }}>
                    {item.name}
                  </span>
                  {isLinkedContent && (
                    <span style={{ 
                      fontSize: '0.65rem', 
                      color: colorScheme.accent,
                      display: 'block',
                      marginTop: '1px'
                    }}>
                      🔗 Linked
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {card.type === 'OUTSIDE_OF_WORK' && (
        <div>
          {(card.content?.items || []).map((item, i, arr) => (
            <div 
              key={i} 
              style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '14px',
                padding: '14px 0',
                paddingTop: i === 0 ? '0' : '14px',
                paddingBottom: i === arr.length - 1 ? '0' : '14px',
                borderBottom: i < arr.length - 1 ? `1px solid ${cssVars.border}` : 'none'
              }}
            >
              {item.icon && (
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: cssVars.bg,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  flexShrink: 0
                }}>
                  {item.icon}
                </div>
              )}
              <p style={{ fontSize: '0.9rem', color: cssVars.textSecondary, lineHeight: '1.5', paddingTop: item.icon ? '0' : '8px' }}>
                {item.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {card.type === 'HIGHLIGHTS' && (
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {(card.content?.items || []).map((item, i) => (
            <li key={i} style={{ fontSize: '0.95rem', color: cssVars.textSecondary, marginBottom: '8px', lineHeight: '1.6' }}>
              {typeof item === 'string' ? item : item.text}
            </li>
          ))}
        </ul>
      )}

      {card.type === 'QUOTE' && (
        <blockquote style={{ borderLeft: `3px solid ${cssVars.text}`, paddingLeft: '16px', margin: 0 }}>
          <p style={{ fontSize: '1rem', fontStyle: 'italic', color: cssVars.text, lineHeight: '1.7' }}>
            &ldquo;{card.content?.text}&rdquo;
          </p>
          {card.content?.author && (
            <cite style={{ display: 'block', fontSize: '0.85rem', color: cssVars.textMuted, marginTop: '12px', fontStyle: 'normal' }}>
              — {card.content.author}
            </cite>
          )}
        </blockquote>
      )}

      {/* Empty Card - renders flexible fields with grouped links */}
      {card.type === 'EMPTY' && (
        <div style={{ 
          padding: '12px', 
          background: cssVars.bg, 
          borderRadius: '12px', 
          border: `1px solid ${cssVars.border}`
        }}>
          {(!card.content?.fields || card.content.fields.length === 0) ? (
            <div style={{ 
              padding: '16px',
              textAlign: 'center',
              color: cssVars.textMuted,
              fontSize: '0.85rem',
              border: `2px dashed ${cssVars.border}`,
              borderRadius: '8px'
            }}>
              Add text, tags, or links
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(() => {
                // Group consecutive links together
                const groupedFields = [];
                let currentLinkGroup = [];
                
                card.content.fields.forEach((field) => {
                  if (field.type === 'link' && field.url) {
                    currentLinkGroup.push(field);
                  } else {
                    if (currentLinkGroup.length > 0) {
                      groupedFields.push({ type: 'linkGroup', links: [...currentLinkGroup] });
                      currentLinkGroup = [];
                    }
                    groupedFields.push(field);
                  }
                });
                
                // Don't forget trailing links
                if (currentLinkGroup.length > 0) {
                  groupedFields.push({ type: 'linkGroup', links: currentLinkGroup });
                }
                
                return groupedFields.map((item, i) => (
                  <div key={i}>
                    {item.type === 'text' && (
                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.9rem', 
                        color: cssVars.text,
                        fontWeight: item.isBold ? '600' : '400',
                        lineHeight: '1.5'
                      }}>
                        {item.value || <span style={{ color: cssVars.textMuted }}>Empty text</span>}
                      </p>
                    )}
                    {item.type === 'tags' && item.value?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {item.value.map((tag, ti) => (
                          <span 
                            key={ti}
                            style={{
                              padding: '4px 10px',
                              background: cssVars.accentLight,
                              color: cssVars.accent,
                              borderRadius: '100px',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {item.type === 'linkGroup' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {item.links.map((link, li) => (
                          <a 
                            key={link.id || li}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 10px',
                              background: cssVars.bgWhite,
                              border: `1px solid ${cssVars.border}`,
                              borderRadius: '8px',
                              color: cssVars.accent,
                              fontSize: '0.8rem',
                              textDecoration: 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                            </svg>
                            {link.value || link.url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* Carousel Card - horizontal scrolling items */}
      {card.type === 'CAROUSEL' && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          overflowX: 'auto', 
          paddingBottom: '8px',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth'
        }}>
          {(card.content?.items || []).length === 0 ? (
            <div style={{ 
              flex: '0 0 100%',
              padding: '32px 16px', 
              background: cssVars.bg, 
              borderRadius: '12px', 
              border: `2px dashed ${cssVars.border}`,
              textAlign: 'center',
              color: cssVars.textMuted,
              fontSize: '0.85rem'
            }}>
              Add items to your carousel
            </div>
          ) : (card.content?.items || []).map((item, i) => (
            <div 
              key={i}
              style={{
                flex: '0 0 160px',
                scrollSnapAlign: 'start',
                background: cssVars.bg,
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              {item.image ? (
                <div style={{ width: '100%', height: '100px', overflow: 'hidden' }}>
                  <img 
                    src={item.image} 
                    alt={item.title || ''} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              ) : item.linkPreview?.image ? (
                <div style={{ width: '100%', height: '100px', overflow: 'hidden' }}>
                  <img 
                    src={item.linkPreview.image} 
                    alt={item.linkPreview.title || ''} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: '100px', 
                  background: `linear-gradient(135deg, ${cssVars.accentLight} 0%, ${cssVars.bg} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="1.5">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
              )}
              <div style={{ padding: '10px 12px' }}>
                <p style={{ 
                  fontSize: '0.8rem', 
                  fontWeight: '500', 
                  color: cssVars.text,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {item.title || item.linkPreview?.title || 'Untitled'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Card Extras (free text, tags, links) */}
      <CardExtrasDisplay extras={card.extras} />
    </div>
  );
}
